// components/settings/CustomInstructionsSettings.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChatBubbleLeftRightIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const CustomInstructionsSettings = ({ settings, onInputChange }) => {
  const highlightsRef = useRef(null);
  const instructionsRef = useRef(null);

  const [localHighlights, setLocalHighlights] = useState(settings.conversation_highlights);
  const [localInstructions, setLocalInstructions] = useState(settings.custom_instructions);
  const [highlightsSaving, setHighlightsSaving] = useState(false);
  const [instructionsSaving, setInstructionsSaving] = useState(false);

  // Update local state when settings change
  useEffect(() => {
    setLocalHighlights(settings.conversation_highlights);
  }, [settings.conversation_highlights]);

  useEffect(() => {
    setLocalInstructions(settings.custom_instructions);
  }, [settings.custom_instructions]);

  // Debounced update to main settings
  const updateHighlights = useCallback(
    debounce((value) => {
      onInputChange("conversation_highlights", value);
    }, 300),
    [onInputChange]
  );

  const updateInstructions = useCallback(
    debounce((value) => {
      onInputChange("custom_instructions", value);
    }, 300),
    [onInputChange]
  );

  const handleHighlightsChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalHighlights(value);
      updateHighlights(value);
    },
    [updateHighlights]
  );

  const handleInstructionsChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalInstructions(value);
      updateInstructions(value);
    },
    [updateInstructions]
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          🎯 AI Conversation Customization
        </h3>
        <p className="text-gray-600 mb-6">
          Customize how your AI assistant handles conversations with specific
          rules and promotional talking points.
        </p>

        <div className="space-y-8">
          {/* Conversation Highlights Section */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-green-900">
                  💬 Conversation Highlights
                </h4>
                <p className="text-sm text-green-700">
                  Things your AI should naturally work into conversations
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-green-800 mb-3">
                Key Points to Mention During Calls
              </label>
              <p className="text-sm text-green-700 mb-4">
                Add promotions, unique selling points, or important
                information you want your AI to naturally bring up when
                relevant. The AI will find appropriate moments to mention
                these.
              </p>

              <div className="mb-4 p-4 bg-white border border-green-200 rounded-lg">
                <h5 className="text-sm font-medium text-green-800 mb-2">
                  💡 Examples to inspire you:
                </h5>
                <ul className="text-xs text-green-700 space-y-1">
                  <li>• We're running a 20% discount on all HVAC tune-ups through July</li>
                  <li>• We offer free estimates and our technicians are all licensed and bonded</li>
                  <li>• We've been family-owned and serving Atlanta for over 25 years</li>
                  <li>• All our work comes with a 2-year warranty</li>
                  <li>• We have same-day emergency service available 24/7</li>
                  <li>• Ask about our maintenance plans - they save customers hundreds per year</li>
                  <li>• Our Google reviews are 4.9 stars with over 500+ happy customers</li>
                  <li>• We price match any legitimate competitor quote</li>
                  <li>• Senior citizens and veterans get 10% off all services</li>
                </ul>
              </div>

              <textarea
                ref={highlightsRef}
                value={localHighlights}
                onChange={handleHighlightsChange}
                className="w-full border border-green-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-sm resize-vertical"
                rows={8}
                placeholder="Enter your talking points here... (one per line)"
                maxLength={800}
              />

              <div className="mt-3 text-xs text-green-700 flex items-center justify-between">
                <div>
                  <p><strong>How this works:</strong></p>
                  <ul className="mt-1 ml-4 space-y-1">
                    <li>• AI will naturally work these into conversations when relevant</li>
                    <li>• Perfect for promotions, warranties, unique selling points</li>
                    <li>• Helps ensure important info gets communicated</li>
                    <li>• AI will choose the right moment - not forced or awkward</li>
                  </ul>
                </div>
                {highlightsSaving && (
                  <div className="text-green-600 flex items-center space-x-1">
                    <ClockIcon className="h-3 w-3 animate-spin" />
                  </div>
                )}
              </div>
            </div>

            {settings.conversation_highlights && (
              <div className="mt-4 bg-white border border-green-200 rounded-lg p-3">
                <h5 className="font-medium text-green-900 mb-2">
                  ✨ Your Saved Talking Points
                </h5>
                <div className="text-sm text-green-800 whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {settings.conversation_highlights}
                </div>
                <p className="text-xs text-green-600 mt-2">
                  💾 This shows your last saved version. Type above to edit.
                </p>
              </div>
            )}
          </div>

          {/* Business Rules Section */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900">
                  📋 Business Rules & Restrictions
                </h4>
                <p className="text-sm text-blue-700">
                  Specific rules and limitations your AI must follow
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-800 mb-3">
                Custom Business Rules & Instructions
              </label>
              <p className="text-sm text-blue-700 mb-4">
                Add specific rules, restrictions, or policies your AI must
                follow. These override general guidelines.
              </p>

              <div className="mb-4 p-4 bg-white border border-blue-200 rounded-lg">
                <h5 className="text-sm font-medium text-blue-800 mb-2">
                  📋 Example business rules:
                </h5>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• We are closed July 4th and July 5th - tell customers we'll call back July 6th</li>
                  <li>• We don't service areas outside of Downtown and Midtown Atlanta</li>
                  <li>• Emergency calls after 10 PM have a $200 after-hours fee</li>
                  <li>• Don't book same-day appointments on Fridays</li>
                  <li>• We require a credit card on file before scheduling</li>
                  <li>• Commercial customers need to provide business license number</li>
                  <li>• Payment is due upon completion - we accept cash, check, or card</li>
                  <li>• For jobs over $500, we require 50% deposit to schedule</li>
                </ul>
              </div>

              <textarea
                ref={instructionsRef}
                value={localInstructions}
                onChange={handleInstructionsChange}
                className="w-full border border-blue-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm resize-vertical"
                rows={8}
                placeholder="Enter your business rules here... (one per line)"
                maxLength={800}
              />

              <div className="mt-3 text-xs text-blue-700 flex items-center justify-between">
                <div>
                  <p><strong>Best for:</strong></p>
                  <ul className="mt-1 ml-4 space-y-1">
                    <li>• Schedule restrictions and blackout dates</li>
                    <li>• Service area limitations</li>
                    <li>• Pricing policies and fees</li>
                    <li>• Legal requirements and disclaimers</li>
                    <li>• Payment and scheduling policies</li>
                  </ul>
                </div>
                {instructionsSaving && (
                  <div className="text-blue-600 flex items-center space-x-1">
                    <ClockIcon className="h-3 w-3 animate-spin" />
                  </div>
                )}
              </div>
            </div>

            {settings.custom_instructions && (
              <div className="mt-4 bg-white border border-blue-200 rounded-lg p-3">
                <h5 className="font-medium text-blue-900 mb-2">
                  📋 Your Saved Business Rules
                </h5>
                <div className="text-sm text-blue-800 whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {settings.custom_instructions}
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  💾 This shows your last saved version. Type above to edit.
                </p>
              </div>
            )}
          </div>

          {/* Examples Section */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
            <h4 className="font-medium text-purple-900 mb-3">
              🎯 How Your AI Uses These
            </h4>
            <div className="space-y-4 text-sm">
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <p className="font-medium text-purple-900 mb-2">
                  💬 Conversation Highlights Example:
                </p>
                <p className="text-purple-800 italic">
                  "I can get you scheduled for next Tuesday. By the way, all
                  our work comes with a 2-year warranty, and we're currently
                  running a 20% discount on tune-ups if you're interested in
                  that as well."
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <p className="font-medium text-purple-900 mb-2">
                  📋 Business Rules Example:
                </p>
                <p className="text-purple-800 italic">
                  "I'd love to help you today, but we're actually closed July
                  4th and 5th for the holiday. I can schedule you for first
                  thing July 6th, or if it's an emergency, we do have an
                  after-hours service with a $200 fee."
                </p>
              </div>
            </div>
          </div>

          {/* Character counts */}
          <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
            <div className="text-center">
              <span className="font-medium">Highlights: </span>
              <span>{localHighlights.length} characters</span>
            </div>
            <div className="text-center">
              <span className="font-medium">Rules: </span>
              <span>{localInstructions.length} characters</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CustomInstructionsSettings);
